import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import { projectAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const openModal = (project) => {
    setSelectedProject(project);
    setShowModal(true);
    setCurrentImage(0);
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await projectAPI.getAll(filter === 'all' ? null : filter);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Projeler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    window.scrollTo(0, 0);
    fetchProjects();
  }, [filter]);


  const categories = [
    { value: 'all', label: 'Tümü' },
    { value: 'klima', label: 'Klima' },
    { value: 'kombi', label: 'Kombi' },
    { value: 'beyaz-esya', label: 'Beyaz Eşya' },
  ];

  return (
    <>
      <Helmet>
        <title>İşlerimiz - Aden İklimlendirme</title>
        <meta name="description" content="Aden İklimlendirme referans çalışmaları. Klima, kombi ve beyaz eşya projelerimiz." />
      </Helmet>

      <Navbar />

      <section className="py-20 md:py-32 bg-light" data-testid="projects-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">İşlerimiz</h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Gerçekleştirdiğimiz projelerden bazılarını buradan inceleyebilirsiniz.
            </p>
          </div>

          {/* Filter */}
          <div className="flex justify-center mb-12 flex-wrap gap-3" data-testid="projects-filter">
            {categories.map((cat) => (
              <button
                key={cat.value}
                data-testid={`filter-${cat.value}`}
                onClick={() => setFilter(cat.value)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${filter === cat.value
                  ? 'bg-orange text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-navy border-t-transparent"></div>
              <p className="mt-4 text-slate-600">Yükleniyor...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20" data-testid="no-projects">
              <p className="text-lg text-slate-600">Henüz proje eklenmemiş.</p>
            </div>
          ) : (

            <div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              data-testid="projects-grid"
            >
              {projects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`project-card-${project.id}`}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >

                  {/* SLIDER */}
                  <div className="h-64">
                    <Swiper
                      modules={[Navigation, Pagination]}
                      navigation
                      pagination={{ clickable: true }}
                      spaceBetween={10}
                      slidesPerView={1}
                      loop={true}
                      className="h-full
    [&_.swiper-button-next]:text-orange-500
    [&_.swiper-button-prev]:text-orange-500
    [&_.swiper-pagination-bullet-active]:bg-orange-500
  "
                    >
                      {(project.images || []).map((img, i) => (
                        <SwiperSlide key={i}>
                          <img
                            src={img}
                            onClick={() => openModal(project)}
                            alt={project.title}
                            className="w-full h-full object-cover"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>


                  {/* CONTENT */}
                  <div className="p-6"
                    onClick={() => openModal(project)} // ✅ Swiper yutsa bile çalışır
                  >
                    <h3 className="text-xl font-bold text-navy mb-2">
                      {project.title}
                    </h3>

                    <p className="text-slate-600 mb-4">
                      {project.description}
                    </p>

                    <span className="inline-block bg-light text-navy px-3 py-1 rounded text-sm font-semibold">
                      {project.service_category}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />

      <Dialog
        open={showModal}
        onOpenChange={(val) => {
          setShowModal(val);
          if (!val) {
            setSelectedProject(null);
            setCurrentImage(0);
          }
        }}
      >
        <DialogContent className="max-w-4xl">

          {selectedProject && (
            <>
              {/* TITLE */}
              <h2 className="text-2xl font-bold mb-4">
                {selectedProject.title}
              </h2>

              {/* SLIDER */}
              <div className="relative">

                {/* IMAGE */}
                <img
                  src={selectedProject.images[currentImage]}
                  className="w-full h-[500px] object-cover rounded-lg"
                />

                {/* LEFT */}
                {selectedProject.images.length > 1 && (
                  <button
                    onClick={() =>
                      setCurrentImage((prev) =>
                        prev === 0
                          ? selectedProject.images.length - 1
                          : prev - 1
                      )
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 text-white px-3 py-2 rounded"
                  >
                    ‹
                  </button>
                )}

                {/* RIGHT */}
                {selectedProject.images.length > 1 && (
                  <button
                    onClick={() =>
                      setCurrentImage((prev) =>
                        prev === selectedProject.images.length - 1
                          ? 0
                          : prev + 1
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 text-white px-3 py-2 rounded"
                  >
                    ›
                  </button>
                )}

              </div>

              {/* DOTS */}
              {selectedProject.images.length > 1 && (
                <div className="flex justify-center mt-4 gap-2">
                  {selectedProject.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImage(i)}
                      className={`w-3 h-3 rounded-full ${i === currentImage
                        ? "bg-orange"
                        : "bg-slate-300"
                        }`}
                    />
                  ))}
                </div>
              )}

              {/* DESC */}
              <p className="mt-4 text-slate-600">
                {selectedProject.description}
              </p>
            </>
          )}

        </DialogContent>
      </Dialog>

    </>
  );
};

export default ProjectsPage;
